import { useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import { DEMO_USERS } from "@/contexts/UsersContext";
import {
	formatDateOnly,
	mapActionViewToAction,
} from "../api/mappers/actionMapper";
import * as actionService from "../api/services/actionService";
import { getCurrentUser } from "../api/services/userService";
import type {
	Action,
	ActionAuditEntry,
	ActionComment,
	CompleteActionPayload,
	CreateActionPayload,
	UpdateActionPayload,
} from "../types/actions";
import { useApp } from "./AppContext";

interface ActionsContextType {
	actions: Action[];
	effectiveUserId: string;
	createAction: (payload: CreateActionPayload) => Action;
	updateAction: (payload: UpdateActionPayload) => void;
	completeAction: (payload: CompleteActionPayload) => void;
	reopenAction: (actionId: string) => void;
	archiveAction: (actionId: string) => void;
	reinstateAction: (actionId: string) => void;
	addActionComment: (actionId: string, text: string) => void;
	// Derived counts for nav badge
	myOpenActionCount: number; // actions assigned TO current persona that are open
}

const ActionsContext = createContext<ActionsContextType | undefined>(undefined);

// ── Demo seed data ────────────────────────────────────────────────────────────

const today = new Date();
today.setHours(0, 0, 0, 0);

const daysFromNow = (n: number) => {
	const d = new Date(today);
	d.setDate(d.getDate() + n);
	return d;
};

const daysAgo = (n: number) => {
	const d = new Date(today);
	d.setDate(d.getDate() - n);
	return d;
};

const makeAudit = (
	actionId: string,
	actorId: string,
	actorName: string,
	eventType: ActionAuditEntry["eventType"],
	detail?: string,
	ts?: Date,
): ActionAuditEntry => ({
	id: `audit-${actionId}-${eventType}-${Date.now()}-${Math.random()}`,
	actionId,
	timestamp: ts ?? new Date(),
	actorId,
	actorName,
	eventType,
	detail,
});

const SEED_ACTIONS: Action[] = [
	{
		id: "act-001",
		title: "Chase overdue invoice — Mersey Build Group",
		description:
			"Invoice INV-2024-089 is 31 days overdue (£28,500). Contact accounts payable and request ETA on payment. Escalate if no response within 48 hours.",
		assignedTo: "3",
		assignedBy: "6",
		dueDate: daysFromNow(1),
		status: "open",
		createdAt: daysAgo(2),
		relatedInvoiceId: "inv-003",
		relatedInvoiceRef: "INV-2024-089",
		relatedCustomer: "Mersey Build Group",
		comments: [
			{
				id: "cmt-001-1",
				actionId: "act-001",
				authorId: "6",
				authorName: "Sarah Mitchell",
				authorRole: "Finance Manager",
				text: "I've tried calling their accounts payable twice this week — no answer. Try emailing directly and copy their MD if no response by end of day.",
				createdAt: daysAgo(1),
			},
			{
				id: "cmt-001-2",
				actionId: "act-001",
				authorId: "3",
				authorName: "Edna Foster",
				authorRole: "Accounts Assistant",
				text: "Email sent to their AP team and MD this morning. Waiting to hear back.",
				createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
			},
		],
		auditLog: [
			makeAudit(
				"act-001",
				"6",
				"Sarah Mitchell",
				"created",
				"Action created",
				daysAgo(2),
			),
		],
	},
	{
		id: "act-002",
		title: "Verify Norton Homes payment cleared",
		description:
			"Invoice INV-2024-091 was marked as pending. Confirm the £18,750 has cleared in the bank and reconcile against the Main Operating Account.",
		assignedTo: "3",
		assignedBy: "6",
		dueDate: daysFromNow(2),
		status: "open",
		createdAt: daysAgo(1),
		relatedInvoiceRef: "INV-2024-091",
		relatedCustomer: "Norton Homes",
		comments: [],
		auditLog: [
			makeAudit(
				"act-002",
				"6",
				"Sarah Mitchell",
				"created",
				"Action created",
				daysAgo(1),
			),
		],
	},
	{
		id: "act-003",
		title: "Chase overdue invoice — Peak District Developments",
		description:
			"Invoice INV-2024-087 is 22 days overdue (£16,200). Send a formal payment reminder and copy in the project manager.",
		assignedTo: "3",
		assignedBy: "6",
		dueDate: daysFromNow(3),
		status: "open",
		createdAt: daysAgo(3),
		relatedInvoiceId: "inv-004",
		relatedInvoiceRef: "INV-2024-087",
		relatedCustomer: "Peak District Developments",
		comments: [
			{
				id: "cmt-003-1",
				actionId: "act-003",
				authorId: "3",
				authorName: "Edna Foster",
				authorRole: "Accounts Assistant",
				text: "Their PM says the client hasn't signed off the last milestone yet so they're holding payment. Should we involve our PM to chase the sign-off?",
				createdAt: daysAgo(1),
			},
		],
		auditLog: [
			makeAudit(
				"act-003",
				"6",
				"Sarah Mitchell",
				"created",
				"Action created",
				daysAgo(3),
			),
		],
	},
	{
		id: "act-004",
		title: "Confirm Q3 subcontractor liability figure",
		description:
			"Board pack needs a confirmed subcontractor liability number for Q3. Please pull from the latest supplier statements and confirm total outstanding.",
		assignedTo: "6",
		assignedBy: "4",
		dueDate: daysFromNow(4),
		status: "open",
		createdAt: daysAgo(1),
		comments: [],
		auditLog: [
			makeAudit(
				"act-004",
				"4",
				"Linda Kowalski",
				"created",
				"Action created",
				daysAgo(1),
			),
		],
	},
	{
		id: "act-006",
		title: "Review October payroll forecast variance",
		description:
			"October payroll came in £4,200 above forecast. Review the breakdown and update the rolling forecast accordingly before the board pack deadline.",
		assignedTo: "6",
		assignedBy: "4",
		dueDate: daysFromNow(2),
		status: "open",
		createdAt: daysAgo(1),
		comments: [],
		auditLog: [
			makeAudit(
				"act-006",
				"4",
				"Linda Kowalski",
				"created",
				"Action created",
				daysAgo(1),
			),
		],
	},
	{
		id: "act-007",
		title: "Approve payment run — week ending Friday",
		description:
			"Review and approve the weekly supplier payment run totalling £62,400. Confirm cash position allows for full payment before authorising.",
		assignedTo: "6",
		assignedBy: "4",
		dueDate: daysFromNow(3),
		status: "open",
		createdAt: daysAgo(2),
		comments: [],
		auditLog: [
			makeAudit(
				"act-007",
				"4",
				"Linda Kowalski",
				"created",
				"Action created",
				daysAgo(2),
			),
		],
	},
	{
		id: "act-008",
		title: "Update cash flow forecast for site delay",
		description:
			"The Castleford site has been delayed by 3 weeks. Adjust the project cash inflows in the forecast and flag the impact on the 90-day position.",
		assignedTo: "6",
		assignedBy: "4",
		dueDate: daysFromNow(1),
		status: "open",
		createdAt: daysAgo(1),
		comments: [],
		auditLog: [
			makeAudit(
				"act-008",
				"4",
				"Linda Kowalski",
				"created",
				"Action created",
				daysAgo(1),
			),
		],
	},
	{
		id: "act-009",
		title: "Liaise with bank re: overdraft facility renewal",
		description:
			"The current overdraft facility expires next month. Contact Barclays relationship manager to begin renewal discussions and confirm terms.",
		assignedTo: "6",
		assignedBy: "4",
		dueDate: daysFromNow(7),
		status: "open",
		createdAt: daysAgo(3),
		comments: [],
		auditLog: [
			makeAudit(
				"act-009",
				"4",
				"Linda Kowalski",
				"created",
				"Action created",
				daysAgo(3),
			),
		],
	},
	{
		id: "act-005",
		title: "Reconcile Jewson account — September statement",
		description:
			"The September Jewson statement has come in. Please reconcile against our records and flag any discrepancies.",
		assignedTo: "3",
		assignedBy: "6",
		dueDate: daysFromNow(5),
		status: "open",
		createdAt: daysAgo(4),
		comments: [],
		auditLog: [
			makeAudit(
				"act-005",
				"6",
				"Sarah Mitchell",
				"created",
				"Action created",
				daysAgo(4),
			),
		],
	},
	// Seed archived action so the archived tab has demo content
	{
		id: "act-010",
		title: "Review supplier payment terms — Keystone Materials",
		description:
			"Keystone have requested a change to 45-day terms. Review and decide whether to accept.",
		assignedTo: "6",
		assignedBy: "4",
		dueDate: daysAgo(10),
		status: "archived",
		createdAt: daysAgo(20),
		archivedAt: daysAgo(5),
		archivedById: "4",
		archivedByName: "Linda Kowalski",
		retainUntil: (() => {
			const d = daysAgo(5);
			d.setFullYear(d.getFullYear() + 1);
			return d;
		})(),
		comments: [],
		auditLog: [
			makeAudit(
				"act-010",
				"4",
				"Linda Kowalski",
				"created",
				"Action created",
				daysAgo(20),
			),
			makeAudit(
				"act-010",
				"4",
				"Linda Kowalski",
				"archived",
				"Archived by Linda Kowalski",
				daysAgo(5),
			),
		],
	},
];

// ─────────────────────────────────────────────────────────────────────────────

export const ActionsProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const { currentUser, mode } = useApp();
	const isDemo = mode === "demo";
	const queryClient = useQueryClient();

	// ── Live mode: resolve real backend user ID ────────────────────────────────
	const { data: liveUserData } = useQuery({
		queryKey: ["currentUser"],
		queryFn: getCurrentUser,
		enabled: !isDemo,
		staleTime: 5 * 60 * 1000,
	});
	const liveUserId = liveUserData?.id;
	const effectiveUserId = isDemo
		? currentUser.id
		: (liveUserId ?? currentUser.id);

	// ── Demo state ──────────────────────────────────────────────────────────────
	const [demoActions, setDemoActions] = useState<Action[]>(SEED_ACTIONS);

	const getUser = (id: string) =>
		DEMO_USERS.find((u) => u.id === id) ?? currentUser;

	// ── Live mode: fetch actions via GraphQL ────────────────────────────────────
	const { data: assignedToMeData } = useQuery({
		queryKey: ["actions", "assignee", liveUserId],
		queryFn: () =>
			actionService.getActionViewList({
				assigneeId: { eq: liveUserId as string },
			}),
		enabled: !isDemo && !!liveUserId,
		staleTime: 30_000,
	});

	const { data: assignedByMeData } = useQuery({
		queryKey: ["actions", "author", liveUserId],
		queryFn: () =>
			actionService.getActionViewList({
				authorId: { eq: liveUserId as string },
			}),
		enabled: !isDemo && !!liveUserId,
		staleTime: 30_000,
	});

	const liveActions = useMemo(() => {
		if (isDemo) return [];
		const seen = new Set<string>();
		const merged: Action[] = [];

		const addEdges = (
			edges: typeof assignedToMeData extends { edges: infer E } ? E : never,
		) => {
			if (!edges) return;
			for (const edge of edges) {
				const action = mapActionViewToAction(edge.node);
				if (!seen.has(action.id)) {
					seen.add(action.id);
					merged.push(action);
				}
			}
		};

		addEdges(assignedToMeData?.edges);
		addEdges(assignedByMeData?.edges);

		return merged;
	}, [isDemo, assignedToMeData, assignedByMeData]);

	const actions = isDemo ? demoActions : liveActions;

	// ── Helpers ─────────────────────────────────────────────────────────────────

	const invalidateActionQueries = () => {
		queryClient.invalidateQueries({ queryKey: ["actions"] });
	};

	// ── Mutations ───────────────────────────────────────────────────────────────

	// biome-ignore lint/correctness/useExhaustiveDependencies: getUser and invalidateActionQueries are stable
	const createAction = useCallback(
		(payload: CreateActionPayload): Action => {
			if (!isDemo) {
				const tempAction: Action = {
					id: `temp-${Date.now()}`,
					title: payload.title,
					description: payload.description,
					assignedTo: payload.assignedTo,
					assignedBy: effectiveUserId,
					dueDate: payload.dueDate,
					status: "open",
					createdAt: new Date(),
					comments: [],
					auditLog: [],
					relatedInvoiceId: payload.relatedInvoiceId,
					relatedInvoiceRef: payload.relatedInvoiceRef,
					relatedCustomer: payload.relatedCustomer,
				};

				actionService
					.createAction({
						title: payload.title,
						description: payload.description,
						assigneeId: payload.assignedTo,
						dueDate: formatDateOnly(payload.dueDate),
						tag: payload.relatedInvoiceRef,
					})
					.then(() => {
						invalidateActionQueries();
					});

				return tempAction;
			}

			// Demo mode
			const user = getUser(currentUser.id);
			const newAction: Action = {
				id: `act-${Date.now()}`,
				title: payload.title,
				description: payload.description,
				assignedTo: payload.assignedTo,
				assignedBy: currentUser.id,
				dueDate: payload.dueDate,
				status: "open",
				createdAt: new Date(),
				comments: [],
				auditLog: [
					makeAudit(
						`act-${Date.now()}`,
						currentUser.id,
						user.name,
						"created",
						"Action created",
					),
				],
				relatedInvoiceId: payload.relatedInvoiceId,
				relatedInvoiceRef: payload.relatedInvoiceRef,
				relatedCustomer: payload.relatedCustomer,
			};
			setDemoActions((prev) => [newAction, ...prev]);
			return newAction;
		},
		[currentUser.id, isDemo],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: getUser and invalidateActionQueries are stable
	const updateAction = useCallback(
		(payload: UpdateActionPayload): void => {
			if (!isDemo) {
				const numericId = Number(payload.actionId);
				actionService
					.updateAction(numericId, {
						title: payload.title,
						description: payload.description,
						dueDate: formatDateOnly(payload.dueDate),
					})
					.then(() => {
						invalidateActionQueries();
					});
				return;
			}

			// Demo mode
			const user = getUser(currentUser.id);
			setDemoActions((prev) =>
				prev.map((a) => {
					if (a.id !== payload.actionId) return a;
					const wasReassigned = a.assignedTo !== payload.assignedTo;
					const auditEntry = makeAudit(
						a.id,
						currentUser.id,
						user.name,
						wasReassigned ? "reassigned" : "updated",
						wasReassigned
							? `Reassigned from ${getUser(a.assignedTo).name} to ${getUser(payload.assignedTo).name} — original record archived, new record created`
							: "Action details updated",
					);
					return {
						...a,
						title: payload.title,
						description: payload.description,
						assignedTo: payload.assignedTo,
						dueDate: payload.dueDate,
						auditLog: [...a.auditLog, auditEntry],
					};
				}),
			);
		},
		[currentUser.id, isDemo],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: getUser and invalidateActionQueries are stable
	const addActionComment = useCallback(
		(actionId: string, text: string) => {
			if (!isDemo) {
				const numericId = Number(actionId);
				actionService
					.addActionMessage(numericId, { message: text })
					.then(() => {
						invalidateActionQueries();
					});
				return;
			}

			// Demo mode
			const user = getUser(currentUser.id);
			const comment: ActionComment = {
				id: `cmt-${Date.now()}`,
				actionId,
				authorId: currentUser.id,
				authorName: user.name,
				authorRole: user.forecasterRole,
				text,
				createdAt: new Date(),
			};
			setDemoActions((prev) =>
				prev.map((a) =>
					a.id === actionId ? { ...a, comments: [...a.comments, comment] } : a,
				),
			);
		},
		[currentUser, isDemo],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: getUser and invalidateActionQueries are stable
	const completeAction = useCallback(
		(payload: CompleteActionPayload) => {
			if (!isDemo) {
				const numericId = Number(payload.actionId);
				actionService
					.completeAction(numericId, {
						statusComment: payload.completionComment,
					})
					.then(() => {
						invalidateActionQueries();
					});
				return;
			}

			// Demo mode
			const user = getUser(currentUser.id);
			const completedAt = new Date();
			const retainUntil = new Date(completedAt);
			retainUntil.setFullYear(retainUntil.getFullYear() + 1);
			setDemoActions((prev) =>
				prev.map((a) => {
					if (a.id !== payload.actionId) return a;
					return {
						...a,
						status: "complete" as const,
						completedAt,
						completionComment: payload.completionComment,
						retainUntil,
						auditLog: [
							...a.auditLog,
							makeAudit(
								a.id,
								currentUser.id,
								user.name,
								"completed",
								payload.completionComment,
								completedAt,
							),
						],
					};
				}),
			);
		},
		[currentUser.id, isDemo],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: getUser and invalidateActionQueries are stable
	const reopenAction = useCallback(
		(actionId: string) => {
			if (!isDemo) {
				const numericId = Number(actionId);
				actionService.reopenAction(numericId).then(() => {
					invalidateActionQueries();
				});
				return;
			}

			// Demo mode
			const user = getUser(currentUser.id);
			setDemoActions((prev) =>
				prev.map((a) => {
					if (a.id !== actionId) return a;
					return {
						...a,
						status: "open" as const,
						completedAt: undefined,
						completionComment: undefined,
						retainUntil: undefined,
						auditLog: [
							...a.auditLog,
							makeAudit(
								a.id,
								currentUser.id,
								user.name,
								"reopened",
								"Action reopened",
							),
						],
					};
				}),
			);
		},
		[currentUser.id, isDemo],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: getUser and invalidateActionQueries are stable
	const archiveAction = useCallback(
		(actionId: string) => {
			if (!isDemo) {
				const numericId = Number(actionId);
				actionService.archiveAction(numericId).then(() => {
					invalidateActionQueries();
				});
				return;
			}

			// Demo mode
			const user = getUser(currentUser.id);
			const archivedAt = new Date();
			const retainUntil = new Date(archivedAt);
			retainUntil.setFullYear(retainUntil.getFullYear() + 1);
			setDemoActions((prev) =>
				prev.map((a) => {
					if (a.id !== actionId) return a;
					return {
						...a,
						status: "archived" as const,
						archivedAt,
						archivedById: currentUser.id,
						archivedByName: user.name,
						retainUntil,
						auditLog: [
							...a.auditLog,
							makeAudit(
								a.id,
								currentUser.id,
								user.name,
								"archived",
								`Archived by ${user.name}`,
								archivedAt,
							),
						],
					};
				}),
			);
		},
		[currentUser.id, isDemo],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: getUser and invalidateActionQueries are stable
	const reinstateAction = useCallback(
		(actionId: string) => {
			if (!isDemo) {
				const numericId = Number(actionId);
				actionService.reopenAction(numericId).then(() => {
					invalidateActionQueries();
				});
				return;
			}

			// Demo mode
			const user = getUser(currentUser.id);
			setDemoActions((prev) =>
				prev.map((a) => {
					if (a.id !== actionId) return a;
					return {
						...a,
						status: "open" as const,
						archivedAt: undefined,
						archivedById: undefined,
						archivedByName: undefined,
						retainUntil: undefined,
						auditLog: [
							...a.auditLog,
							makeAudit(
								a.id,
								currentUser.id,
								user.name,
								"reinstated",
								`Reinstated by ${user.name}`,
							),
						],
					};
				}),
			);
		},
		[currentUser.id, isDemo],
	);

	// Count of open actions assigned to the current user
	const myOpenActionCount = actions.filter(
		(a) => a.assignedTo === effectiveUserId && a.status === "open",
	).length;

	return (
		<ActionsContext.Provider
			value={{
				actions,
				effectiveUserId,
				createAction,
				updateAction,
				completeAction,
				reopenAction,
				archiveAction,
				reinstateAction,
				addActionComment,
				myOpenActionCount,
			}}
		>
			{children}
		</ActionsContext.Provider>
	);
};

export const useActions = () => {
	const context = useContext(ActionsContext);
	if (!context)
		throw new Error("useActions must be used within an ActionsProvider");
	return context;
};
